import React, { useEffect, useState } from 'react';

import { useAuthStore } from '../../store/auth';
import { authApi } from '../../api/auth';
import LoadingSpinner from '../common/LoadingSpinner';
import './AuthGuard.css';

const AuthGuard = ({ children }) => {
  const { token, setUser, isAuthenticated } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const fetchMeWithRetry = async (retries = 2) => {
          let lastError = null;
          for (let attempt = 0; attempt <= retries; attempt++) {
            try {
              return await authApi.me();
            } catch (err) {
              lastError = err;
              const msg = (err?.message || '').toLowerCase();
              const isAbort =
                err?.code === 'ERR_CANCELED' ||
                msg.includes('aborted') ||
                msg.includes('cancel') ||
                msg.includes('binding');
              const isNetwork = !err?.response;
              if (isAbort || isNetwork) {
                await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1)));
                continue;
              }
              throw err;
            }
          }
          throw lastError;
        };

        const response = await fetchMeWithRetry(2);
        const userData = response.data.user || response.data;
        setUser(userData);
        setError(null);
      } catch (err) {
        console.error('Erro ao verificar autenticação:', err);

        if (err.response?.status === 401) {
          useAuthStore.getState().clearToken();
          window.location.href = '/login';
          setLoading(false);
          return;
        }

        const hasUser = !!useAuthStore.getState().user;
        if (hasUser && (err.response?.status >= 500 || !err.response)) {
          setError(null);
          setLoading(false);
          return;
        }

        setError('Erro ao carregar dados do usuário');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [token, setUser]);

  if (loading) {
    return <LoadingSpinner size="large" text="Verificando autenticação..." fullScreen />;
  }

  if (error) {
    return (
      <div className="auth-guard-error">
        <div className="auth-guard-error__card">
          <div className="auth-guard-error__icon-wrap">
            <span className="auth-guard-error__icon" aria-hidden>!</span>
          </div>
          <h1 className="auth-guard-error__title">Erro ao carregar sua conta</h1>
          <p className="auth-guard-error__description">
            Não foi possível verificar sua autenticação. Verifique sua conexão e tente novamente.
          </p>
          <div className="auth-guard-error__actions">
            <button
              type="button"
              className="auth-guard-error__button auth-guard-error__button--primary"
              onClick={() => window.location.reload()}
            >
              Tentar novamente
            </button>
            <a href="/login" className="auth-guard-error__button auth-guard-error__button--secondary">
              Ir para Login
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated()) {
    return (
      <div className="auth-guard-unauthorized">
        <div className="auth-guard-unauthorized__card">
          <div className="auth-guard-unauthorized__icon-wrap">
            <span className="auth-guard-unauthorized__icon" aria-hidden>!</span>
          </div>
          <h1 className="auth-guard-unauthorized__title">Acesso Não Autorizado</h1>
          <p className="auth-guard-unauthorized__description">
            Você precisa fazer login para acessar esta página.
          </p>
          <a
            href="/login"
            className="auth-guard-unauthorized__button"
          >
            ← Ir para Login
          </a>
        </div>
      </div>
    );
  }

  return children;
};

export default AuthGuard;
